import { LIBRARY_ITEM_ATTR } from "@/components/library/library-item"

/**
 * Геометрическая навигация по элементам папки: сетка может быть несколькими
 * блоками и колонками разной высоты, поэтому «выше/ниже» ищем по координатам,
 * а не по индексам — так работает и плитка, и список.
 */
function itemNodes(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(`[${LIBRARY_ITEM_ATTR}]`))
}

function keyOf(node: HTMLElement): string | null {
  return node.getAttribute(LIBRARY_ITEM_ATTR)
}

/** Допуск на субпиксели: сосед по горизонтали не считается «ниже». */
const EDGE_PX = 4

export type LibraryFocusMove = "next" | "prev" | "down" | "up" | "first" | "last"

export type LibraryFocusRect = {
  key: string
  top: number
  left: number
  width: number
  height: number
}

function centerX(rect: LibraryFocusRect): number {
  return rect.left + rect.width / 2
}

function overlapsX(a: LibraryFocusRect, b: LibraryFocusRect): boolean {
  return a.left < b.left + b.width - EDGE_PX && b.left < a.left + a.width - EDGE_PX
}

function verticalDistance(rect: LibraryFocusRect, current: LibraryFocusRect, move: "down" | "up"): number {
  if (move === "down") return rect.top - (current.top + current.height)
  return current.top - (rect.top + rect.height)
}

/**
 * Вниз и вверх — сначала карточка, которая пересекает текущую по горизонтали
 * (та, что стоит в той же колонке). Иначе ближайшая по горизонтали среди тех,
 * что целиком ниже или выше. В ровной сетке и в списке это соседняя строка.
 */
function pickVertical(rects: LibraryFocusRect[], index: number, move: "down" | "up"): string | null {
  const current = rects[index]
  if (!current) return null
  const currentBottom = current.top + current.height
  const candidates = rects.filter((rect, rectIndex) => {
    if (rectIndex === index) return false
    if (move === "down") return rect.top >= currentBottom - EDGE_PX
    return rect.top + rect.height <= current.top + EDGE_PX
  })
  if (candidates.length === 0) return null
  const overlapping = candidates.filter((rect) => overlapsX(current, rect))
  const pool = overlapping.length > 0 ? overlapping : candidates
  let best = pool[0]
  if (!best) return null
  for (const rect of pool.slice(1)) {
    const distance = verticalDistance(rect, current, move)
    const bestDistance = verticalDistance(best, current, move)
    if (distance !== bestDistance) {
      if (distance < bestDistance) best = rect
      continue
    }
    if (Math.abs(centerX(rect) - centerX(current)) < Math.abs(centerX(best) - centerX(current))) best = rect
  }
  return best.key
}

/**
 * Ключ элемента после перемещения курсора по уже известным прямоугольникам.
 * Прямоугольники — в DOM-порядке. null — двигаться некуда (край блока).
 */
export function pickLibraryFocusTarget(
  rects: LibraryFocusRect[],
  currentKey: string | null,
  move: LibraryFocusMove,
): string | null {
  if (rects.length === 0) return null
  const index = currentKey == null ? -1 : rects.findIndex((rect) => rect.key === currentKey)

  if (move === "first" || index < 0) return rects[0]?.key ?? null
  if (move === "last") return rects[rects.length - 1]?.key ?? null

  // Влево-вправо — соседи в порядке обхода, без особых случаев на краях рядов.
  if (move === "next") return index < rects.length - 1 ? (rects[index + 1]?.key ?? null) : null
  if (move === "prev") return index > 0 ? (rects[index - 1]?.key ?? null) : null

  return pickVertical(rects, index, move)
}

/** Ключ элемента после перемещения курсора; null — двигаться некуда (край блока). */
export function moveLibraryFocus(
  container: HTMLElement,
  currentKey: string | null,
  move: LibraryFocusMove,
): string | null {
  const rects: LibraryFocusRect[] = []
  for (const node of itemNodes(container)) {
    const key = keyOf(node)
    if (!key) continue
    const rect = node.getBoundingClientRect()
    rects.push({ key, top: rect.top, left: rect.left, width: rect.width, height: rect.height })
  }
  return pickLibraryFocusTarget(rects, currentKey, move)
}

function findNode(container: HTMLElement | null, key: string | null): HTMLElement | null {
  if (!container || !key) return null
  return container.querySelector<HTMLElement>(`[${LIBRARY_ITEM_ATTR}="${CSS.escape(key)}"]`)
}

/** Ставит реальный фокус на элемент с этим ключом (плюс подскроллить к нему). */
export function focusLibraryItem(container: HTMLElement | null, key: string | null): void {
  const node = findNode(container, key)
  if (!node) return
  node.focus({ preventScroll: true })
  node.scrollIntoView({ block: "nearest", inline: "nearest" })
}

/** Ключ элемента, с которого начинать обход блока (первый в DOM-порядке). */
export function firstLibraryItemKey(container: HTMLElement | null): string | null {
  if (!container) return null
  const nodes = itemNodes(container)
  return nodes.length > 0 ? keyOf(nodes[0]) : null
}

/** Есть ли такой элемент сейчас на экране (после смены папки ключ мог устареть). */
export function hasLibraryItem(container: HTMLElement | null, key: string | null): boolean {
  return findNode(container, key) != null
}

/** Enter по элементу: сам элемент решает, что делать — открыться или раскрыть группу. */
export function activateLibraryItem(container: HTMLElement | null, key: string | null): void {
  findNode(container, key)?.click()
}
